import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, Note, Participant, SourceData, SourceDataReview, SourceDataVerification, Trial } from "@/api/dtos";
import useEvent from "@/hooks/form/source-data/useEvent";
import useForm from "@/hooks/form/source-data/useForm";
import useItemsVirtualized from "@/hooks/form/source-data/useItemsVirtualized";
import { Paper } from "@mui/material";
import { FC, useMemo, useState } from "react";
import ReviewAndVerificationForm from "../../ReviewAndVerificationForm";
import SourceDataFormVirtualizedList from "./lists/SourceDataFormVirtualizedList";
import SourceDataFormReviewAndVerificationAllButton from "./buttons/SourceDataFormReviewAndVerificationAllButton";
import useEventConfiguration from "@/hooks/form/configuration/useEventConfiguration";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import useFormVersionConfiguration from "@/hooks/form/configuration/useFormVersionConfiguration";
import useSourceDataItemGroupGrid from "@/hooks/form/source-data/useSourceDataItemGroupGrid";
import SourceDataItemGroupGrid from "./grids/SourceDataItemGroupGrid";
import SourceDataItemGroupGridContext from "@/contexts/form/SourceDataItemGroupGridContext";


interface ISourceDataFormProps {
    trial: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant: Participant;
    metadata: Metadata;
    trialConfiguration: ConfigurationSourceDataMap;
    configuration: ConfigurationSourceDataMap;
    sourceData?: SourceData;
    studyEventOid: string;
    studyEventRepeat: number;
    caseReportFormOid: string;
    caseReportFormVersionOid: string;
    notesAndQueries?: Record<string, Record<number, Record<string, Record<string, Record<number, Record<string, Note[]>>>>>>;
    preview?: boolean;
    reviews?: Record<string, Record<number, Record<string, Record<string, Record<number, Record<string, SourceDataReview>>>>>>;
    reviewsUpdating?: boolean;
    verifications?: Record<string, Record<number, Record<string, Record<string, Record<number, Record<string, SourceDataVerification>>>>>>;
    verificationsUpdating?: boolean;

    form: ReturnType<typeof useForm>;
    event: ReturnType<typeof useEvent>;
}


const SourceDataForm: FC<ISourceDataFormProps> = ({
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    metadata,
    trialConfiguration,
    configuration,
    sourceData,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    event,
    form,
    preview
}) => {

    // const itemData = useItems(
    //     metadata,
    //     trialConfiguration,
    //     configuration,
    //     sourceData,
    //     trial,
    //     masterGroup,
    //     coordinatingGroup,
    //     institution,
    //     participant,
    //     studyEventOid,
    //     studyEventRepeat,
    //     caseReportFormOid,
    //     caseReportFormVersionOid,
    //     preview
    // );

    const itemData = useItemsVirtualized(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        preview
    );


    const eventConfiguration = useEventConfiguration(
        metadata,
        trialConfiguration,
        configuration,
        trial,
        configuration?.configuration?.id,
        studyEventOid
    );

    const formConfiguration = useFormConfiguration(
        metadata,
        trialConfiguration,
        configuration,
        trial,
        configuration?.configuration?.id,
        studyEventOid,
        caseReportFormOid
    );

    const formVersionConfiguration = useFormVersionConfiguration(
        metadata,
        trialConfiguration,
        configuration,
        trial,
        configuration?.configuration?.id,
        studyEventOid,
        caseReportFormOid,
        caseReportFormVersionOid
    );

    const allowed = useMemo(() => {
        const allowReview = eventConfiguration?.configuration?.allowReview === true && 
            formConfiguration?.configuration?.allowReview === true &&
            formVersionConfiguration?.configuration?.allowReview === true;

        const allowVerification = eventConfiguration?.configuration?.allowVerification === true && 
            formConfiguration?.configuration?.allowVerification === true &&
            formVersionConfiguration?.configuration?.allowVerification === true;

        return {
            allowReview,
            allowVerification
        }
    }, [eventConfiguration, formConfiguration, formVersionConfiguration])

    const [itemGroupName, setItemGroupName] = useState<string | undefined>();

    const context = useMemo(() => {
        return {
            itemGroupName, 
            setItemGroupName
        }
    }, [itemGroupName, setItemGroupName])

    if (!itemData) {
        return null;
    }

    return (
        <ReviewAndVerificationForm
            trialId={trial?.id}
            masterGroupId={masterGroup?.id}
            coordinatingGroupId={coordinatingGroup?.id}
            institutionId={institution?.id}
            participantId={participant?.id}
            studyEventOid={studyEventOid}
            studyEventRepeat={studyEventRepeat}
            caseReportFormOid={caseReportFormOid}
        >
            <SourceDataItemGroupGrid 
                metadata={metadata}
                trialConfiguration={trialConfiguration}
                configuration={configuration}
                sourceData={sourceData}
                trial={trial}
                masterGroup={masterGroup}
                coordinatingGroup={coordinatingGroup}
                institution={institution}
                participant={participant}
                studyEventOid={studyEventOid}
                studyEventRepeat={studyEventRepeat}
                caseReportFormOid={caseReportFormOid}
                caseReportFormVersionOid={caseReportFormVersionOid}
                itemGroupName={itemGroupName}
                preview={preview}
                onClose={() => setItemGroupName(undefined)}
            />
            <SourceDataFormReviewAndVerificationAllButton
                form={form}
                trial={trial}
                masterGroup={masterGroup}
                coordinatingGroup={coordinatingGroup}
                institution={institution}
                participant={participant}
                studyEventOid={studyEventOid}
                studyEventRepeat={studyEventRepeat}
                caseReportFormOid={caseReportFormOid}
                caseReportFormVersionOid={caseReportFormVersionOid}
                itemData={itemData}
                allowReview={allowed.allowReview}
                allowVerification={allowed.allowVerification}
            />
            <SourceDataItemGroupGridContext.Provider value={context}>
                <Paper>
                    {/* <SourceDataItemList itemData={itemData} /> */}
                    <SourceDataFormVirtualizedList 
                        itemData={itemData} 
                        form={form} 
                        event={event} 
                        allowReview={allowed.allowReview}
                        allowVerification={allowed.allowVerification}
                    />
                </Paper>
            </SourceDataItemGroupGridContext.Provider>
        </ReviewAndVerificationForm>
    )
};

export default SourceDataForm;